import { Seo } from '../components/Seo'
import { PageHeader } from '../components/common/PageHeader'
import { company } from '../data/company'
import { breadcrumbSchema } from '../lib/seo'

type Doc = 'privacy' | 'terms' | 'disclaimer'

interface LegalSection {
  heading: string
  body: string[]
}
interface LegalContent {
  path: string
  label: string
  title: string
  intro: string
  sections: LegalSection[]
}

const LAST_UPDATED = 'July 2026'

const CONTENT: Record<Doc, LegalContent> = {
  privacy: {
    path: '/privacy-policy',
    label: 'Privacy Policy',
    title: 'Privacy Policy',
    intro:
      'This policy explains how the public GreenbayBio website handles the information you choose to share with us.',
    sections: [
      {
        heading: 'Information we collect',
        body: [
          'When you submit an enquiry, we ask for your name and phone number, and optionally your email, location, the product you’re interested in, and your message. We only ask for what we need to respond to you.',
          'These details are used solely to respond to your enquiry — to reach you about the product, agronomic advice, or partnership you asked about. We do not use them for anything else.',
        ],
      },
      {
        heading: 'How we use your information',
        body: [
          'To respond to your product, farmer, retailer or distributor enquiry, and to provide the information or support you have requested.',
          'We do not sell your personal information, and we do not share it except as needed to respond to you or where required by law.',
        ],
      },
      {
        heading: 'Cookies & analytics',
        body: ['This website does not require advertising cookies to function. Any analytics used will be limited to understanding site usage in aggregate.'],
      },
      {
        heading: 'Contact',
        body: [`For any privacy question, contact us at ${company.email} or ${company.phoneDisplay}.`],
      },
    ],
  },
  terms: {
    path: '/terms',
    label: 'Terms of Use',
    title: 'Terms of Use',
    intro: 'By using this website you agree to the following terms.',
    sections: [
      {
        heading: 'Use of this website',
        body: [
          'This site is provided for general information about GreenbayBio and its products. You may browse and share it for lawful, non-commercial purposes.',
          'All content, branding and imagery on this site belong to their respective owners and may not be reproduced without permission.',
        ],
      },
      {
        heading: 'Product information',
        body: [
          'We aim to keep product information accurate and up to date, drawn from our official product documentation. Always follow the instructions, dosage and precautions printed on the product label.',
          'Product availability, pack sizes and approvals may change over time.',
        ],
      },
      {
        heading: 'No warranty of results',
        body: [
          'Information on this site is provided “as is”, without any warranty as to specific outcomes. Crop results depend on many factors outside our control.',
        ],
      },
      {
        heading: 'Governing law',
        body: ['These terms are governed by the laws of India, and any disputes are subject to the jurisdiction of the courts of Kolkata.'],
      },
    ],
  },
  disclaimer: {
    path: '/disclaimer',
    label: 'Disclaimer',
    title: 'Disclaimer',
    intro: 'Please read this disclaimer before relying on any information on this website.',
    sections: [
      {
        heading: 'General information only',
        body: [
          'The agronomic guidance, crop recommendations and dosage summaries on this website are general information to help you get started. They are not a substitute for advice tailored to your specific field, soil and conditions.',
          'Always follow the exact dosage, timing and precautions on the product label, and consult a Greenbay agronomic advisor or a qualified agronomist for your situation.',
        ],
      },
      {
        heading: 'No guarantee of results',
        body: [
          'We make no guarantee of any particular yield, income or outcome. Product performance depends on soil condition, climate, crop management and other factors beyond our control.',
        ],
      },
      {
        heading: 'Approvals & certifications',
        body: [
          'Approvals and certifications (such as NPOP, FCO, EU/NOP and USDA BioPreferred®) apply to specific products as stated on each product page, and may vary by product and region.',
        ],
      },
      {
        heading: 'Imagery',
        body: [
          'Some gallery imagery on this website is represented by branded placeholders and will be replaced with actual photography.',
        ],
      },
    ],
  },
}

export default function LegalPage({ doc }: { doc: Doc }) {
  const c = CONTENT[doc]
  return (
    <>
      <Seo
        title={`${c.label} | GreenbayBio`}
        description={c.intro}
        path={c.path}
        jsonLd={[breadcrumbSchema([{ name: 'Home', path: '/' }, { name: c.label, path: c.path }])]}
      />
      <PageHeader eyebrow="Legal" title={c.title} subtitle={c.intro} />

      <div className="container-x max-w-prose py-14">
        <p className="mb-8 text-sm text-ink-500">Last updated: {LAST_UPDATED}</p>
        <div className="space-y-10">
          {c.sections.map((s) => (
            <section key={s.heading}>
              <h2 className="font-display text-h3 font-semibold text-ink">{s.heading}</h2>
              <div className="mt-3 space-y-3 leading-relaxed text-ink-600">
                {s.body.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </>
  )
}
